import React, { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Flex,
  Form,
  Grid,
  Row,
  Select,
  Skeleton,
  Space,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';

import InternshipApplicationComponent from './InternshipApplicationComponent';
import {
  GetActiveInternshipOffers,
  GetOrganizations,
  GetStudentApplications,
  GetStudyLines,
} from '../../helper/apiHelper';
import type {
  InternshipApplication,
  ReducedInternship,
  ReducedOrganization,
  StudyLine,
} from '../../Types/interfaces';
import Title from 'antd/es/typography/Title';
import { ImageLoader } from './ImageLoader';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useStudent } from '../../hooks/useStudent';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useUserOrganizationsIds } from '../../hooks/useOrganizations';

const { useBreakpoint } = Grid;

const filterByCompany = (
  internships: ReducedInternship[],
  id: number
): ReducedInternship[] => {
  return internships.filter((value) => {
    return value.organizationId === id;
  });
};

const filterByNumberOfHours = (
  internships: ReducedInternship[],
  interval: [number, number]
): ReducedInternship[] => {
  return internships.filter((value) => {
    return (
      value.numberOfHours >= interval[0] && value.numberOfHours <= interval[1]
    );
  });
};

const filterPreferredStudyLines = (
  internships: ReducedInternship[],
  preferredStudyLineIds?: number[]
): ReducedInternship[] => {
  return internships.filter((internship) =>
    internship.internshipSessions.some((session) =>
      preferredStudyLineIds?.includes(session.studyLineId)
    )
  );
};

type OrganizedInternships = {
  [studyLineId: number]: {
    [sessionId: number]: ReducedInternship[];
  };
};

const InternshipListComponent = (): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [formHidden, setFormHidden] = useState(true);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const watchedValues = Form.useWatch([], form);
  const { data: student } = useStudent(loggedInUser.id);
  const screens = useBreakpoint();
  const { data: studentOrganizationsIds } = useUserOrganizationsIds(
    loggedInUser.id
  );

  const results = useQueries({
    queries: [
      { queryKey: QUERY_KEYS.ORGANIZATIONS, queryFn: GetOrganizations },
      {
        queryKey: [QUERY_KEYS.ACTIVE_INTERNSHIP_OFFERS, loggedInUser.id],
        queryFn: async () => {
          const rawInternships = await GetActiveInternshipOffers();
          try {
            const studentApplications: InternshipApplication[] =
              await GetStudentApplications(loggedInUser.id);

            return rawInternships.filter(
              (internship) =>
                !studentApplications.some(
                  (application) =>
                    application.internshipOfferId === internship.id
                )
            );
          } catch {
            return rawInternships;
          }
        },
        enabled: !!loggedInUser.id,
      },
      { queryKey: QUERY_KEYS.STUDY_LINES, queryFn: GetStudyLines },
    ],
  });

  const organizations: ReducedOrganization[] = results[0].data ?? EMPTY_ARRAY;
  const internships: ReducedInternship[] = results[1].data ?? EMPTY_ARRAY;
  const studyLines: StudyLine[] = results[2].data ?? EMPTY_ARRAY;
  const isLoading = results.some((result) => result.isLoading);
  const hasError = results.some((result) => result.isError);

  useEffect(() => {
    if (hasError) {
      messageApi.error({
        content: t('internshipListComponent.fetchError'),
      });
    }
  }, [hasError, messageApi, t]);

  useEffect(() => {
    if (organizations.length === 1) {
      form.setFieldValue('organizationId', organizations[0].id);
    }
  }, [form, organizations]);

  const filteredInternships = useMemo(() => {
    if (!internships || internships.length === 0) return [];

    const values = watchedValues || form.getFieldsValue();
    console.log(values);
    let filterInternships = internships;

    if (values.organizationId !== undefined) {
      filterInternships = filterByCompany(
        filterInternships,
        values.organizationId
      );
    }

    if (values.numberOfHours !== undefined) {
      filterInternships = filterByNumberOfHours(
        filterInternships,
        values.numberOfHours
      );
    }

    if (values.preferredStudyLines === true) {
      filterInternships = filterPreferredStudyLines(
        filterInternships,
        student?.preferredStudyLineIds
      );
    }

    return filterInternships;
  }, [form, internships, watchedValues, student?.preferredStudyLineIds]);

  const { preferredInternships, otherInternships } = useMemo(() => {
    const preferred: OrganizedInternships = {};
    const other: OrganizedInternships = {};

    filteredInternships.forEach((internship) => {
      internship.internshipSessions.forEach((session) => {
        const studyLineId = session.studyLineId;

        // setup preferred and other internships
        if (student?.preferredStudyLineIds.includes(studyLineId)) {
          if (!preferred[studyLineId]) {
            preferred[studyLineId] = {};
          }

          if (!preferred[studyLineId][session.id]) {
            preferred[studyLineId][session.id] = [];
          }

          preferred[studyLineId][session.id].push(internship);
        } else {
          if (!other[studyLineId]) {
            other[studyLineId] = {};
          }

          if (!other[studyLineId][session.id]) {
            other[studyLineId][session.id] = [];
          }

          other[studyLineId][session.id].push(internship);
        }
      });
    });

    return { preferredInternships: preferred, otherInternships: other };
  }, [filteredInternships, student?.preferredStudyLineIds]);

  const getStudyLineName = (studyLineId: number): string => {
    return studyLines.find((s) => s.id === studyLineId)?.name ?? '';
  };

  const getSessionName = (sessionId: number): string => {
    return (
      studyLines
        .flatMap((sl) => sl.internshipSessions)
        .find((s) => s.id === sessionId)?.description ?? ''
    );
  };

  const onReset = (): void => {
    form.resetFields();
  };

  function getOrganizationName(organizationId: number): string | null {
    const organization = organizations.find((o) => o.id === organizationId);
    return organization ? organization.name : null;
  }

  const removeInternship = (id: number): void => {
    queryClient.setQueryData(
      [QUERY_KEYS.ACTIVE_INTERNSHIP_OFFERS, loggedInUser.id],
      (oldData: ReducedInternship[]) => {
        // check if cache is empty
        if (!oldData) return [];
        return oldData.filter((item) => item.id !== id);
      }
    );
  };

  const SkeletonCards: React.ReactElement = (
    <Flex wrap="wrap" gap="middle" style={{ marginTop: '130px' }}>
      {Array(6)
        .fill(null)
        .map((_, i) => (
          <Card
            key={i}
            style={{
              width: 300,
              height: 450,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Skeleton active paragraph={{ rows: 1 }} />
            <Skeleton.Image style={{ height: 200, width: 250 }} active={true} />
          </Card>
        ))}
    </Flex>
  );

  return (
    <>
      {contextHolder}
      <Button onClick={() => setFormHidden(!formHidden)}>
        {t('internshipListComponent.filter.filter')}
      </Button>
      <Row>
        <Col lg={12} xl={12}>
          <Space
            direction="vertical"
            size="middle"
            style={{ display: 'flex', maxWidth: '500px', width: '100%' }}
          >
            <br />
            <Card hidden={formHidden}>
              <Form form={form}>
                <Form.Item name="organizationId">
                  <Select
                    size="large"
                    placeholder={t(
                      'internshipListComponent.filter.organization'
                    )}
                    options={organizations.map((org) => ({
                      label: org.name,
                      value: org.id,
                    }))}
                  />
                </Form.Item>
                <Form.Item>
                  <Button htmlType="button" onClick={onReset}>
                    {t('internshipListComponent.filter.reset')}
                  </Button>
                </Form.Item>

                <Form.Item name="preferredStudyLines" valuePropName="checked">
                  <Checkbox>
                    {t('internshipListComponent.filter.showPrefOnly')}:
                  </Checkbox>
                </Form.Item>
              </Form>
            </Card>
          </Space>
        </Col>
      </Row>

      <Space
        direction="vertical"
        size="middle"
        style={{ display: 'flex', width: '100%' }}
      >
        {/* Preferred Study Lines - Not in a Collapse */}
        {isLoading && SkeletonCards}
        {Object.entries(preferredInternships).map(([studyLineId, sessions]) => (
          <div key={studyLineId} style={{ width: '100%' }}>
            <Title level={screens.lg ? 2 : 4}>
              {t('internshipListComponent.studyLine')}:{' '}
              {getStudyLineName(parseInt(studyLineId))}
            </Title>
            {Object.entries(sessions).map(([sessionId, internships]) => (
              <React.Fragment key={sessionId}>
                <Title level={screens.lg ? 3 : 5} style={{ paddingBottom: 10 }}>
                  {t('internshipListComponent.session')}:{' '}
                  {getSessionName(parseInt(sessionId))}
                </Title>
                <Row
                  gutter={[15, 15]}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  {internships.map((item) => (
                    <Col key={item.id}>
                      <Card
                        title={`${item.name} - ${getOrganizationName(item.organizationId) ?? t('internshipListComponent.na')}`}
                        style={{
                          width: 300,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        }}
                        actions={[
                          <InternshipApplicationComponent
                            key={`internship-application-${item.id}`}
                            internshipId={item.id}
                            sessionId={parseInt(sessionId)}
                            organization={
                              getOrganizationName(item.organizationId) ??
                              t('internshipListComponent.na')
                            }
                            removeInternship={removeInternship}
                            applyable={
                              studentOrganizationsIds?.includes(
                                item.organizationId
                              )
                                ? 'HisOwnCompany'
                                : 'Allowed'
                            }
                            studyLineName={getStudyLineName(
                              parseInt(studyLineId)
                            )}
                          />,
                        ]}
                        cover={<ImageLoader logoData={item.logoData} />}
                      />
                    </Col>
                  ))}
                </Row>
              </React.Fragment>
            ))}
          </div>
        ))}
        <Collapse bordered={false} style={{ width: '100%' }}>
          {Object.entries(otherInternships).map(([studyLineId, sessions]) => (
            <Collapse.Panel
              key={studyLineId}
              header={`${t('internshipListComponent.studyLine')}: ${getStudyLineName(parseInt(studyLineId))}`}
              style={{
                fontSize: 24,
                fontWeight: 500,
                backgroundColor: 'white',
                border: 'none',
              }}
            >
              {Object.entries(
                sessions as { [key: string]: ReducedInternship[] }
              ).map(([sessionId, internships]) => (
                <React.Fragment key={sessionId}>
                  <Title level={3}>
                    {t('internshipListComponent.session')}:{' '}
                    {getSessionName(parseInt(sessionId))}
                  </Title>
                  <Row
                    gutter={[15, 15]}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                    }}
                  >
                    {internships.map((item) => (
                      <Col key={item.id}>
                        <Card
                          title={`${item.name} - ${getOrganizationName(item.organizationId) ?? t('internshipListComponent.na')}`}
                          style={{
                            width: 300,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                          }}
                          actions={[
                            <InternshipApplicationComponent
                              key={`internship-appl-${item.id}`}
                              internshipId={item.id}
                              sessionId={parseInt(sessionId)}
                              organization={
                                getOrganizationName(item.organizationId) ??
                                t('internshipListComponent.na')
                              }
                              removeInternship={removeInternship}
                              applyable={'HeIsntOnThatStudyLine'}
                              studyLineName="Other"
                            />,
                          ]}
                          cover={<ImageLoader logoData={item.logoData} />}
                        />
                      </Col>
                    ))}
                  </Row>
                </React.Fragment>
              ))}
            </Collapse.Panel>
          ))}
        </Collapse>
      </Space>
    </>
  );
};

export default InternshipListComponent;
